import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { AxiosHeaders } from 'axios'
import {
  AUTH_APP_PRODUCT_HEADER,
  AUTH_OMIT_BEARER_RELATIVE_PATHS,
  WORKFORCE_APP_PRODUCT,
  buildLoginRequestBody,
  buildLogoutRequestBody,
  buildRefreshTokenRequestBody,
  normalizeAuthRelativePath,
  parseAuthResponse,
  parseMeSessionSnapshot,
  parseStaffInviteValidation,
  type AuthResponse,
  type LoginRequest,
  type LogoutRequest,
  type MeSessionSnapshot,
  type RefreshTokenRequest,
  type StaffInviteValidation,
} from './auth-types'

export type AuthRouteRequestInterceptorOptions = {
  /** Product line hint for login + refresh. Defaults to `workforce`. */
  appProduct?: string
}

/**
 * Request interceptor matching Flutter `DioAuthInterceptor`:
 * - Strips Bearer on anonymous auth paths (refresh/logout with expired JWT).
 * - Sets `X-App-Product` on login and refresh-token only.
 *
 * Register **after** any Bearer-injection interceptor so omit wins on auth paths.
 */
export function applyAuthRouteRequestHeaders(
  config: InternalAxiosRequestConfig,
  options?: AuthRouteRequestInterceptorOptions,
): InternalAxiosRequestConfig {
  const path = normalizeAuthRelativePath(config.url)
  if (AUTH_OMIT_BEARER_RELATIVE_PATHS.has(path)) {
    delete config.headers.Authorization
  }
  const appProduct = (options?.appProduct ?? WORKFORCE_APP_PRODUCT).trim()
  if (
    appProduct.length > 0 &&
    (path === '/auth/login' || path === '/auth/refresh-token')
  ) {
    config.headers[AUTH_APP_PRODUCT_HEADER] = appProduct
  }
  return config
}

export function registerAuthRouteRequestInterceptor(
  client: AxiosInstance,
  options?: AuthRouteRequestInterceptorOptions,
): () => void {
  const id = client.interceptors.request.use((config) =>
    applyAuthRouteRequestHeaders(config, options),
  )
  return () => {
    client.interceptors.request.eject(id)
  }
}

function workforceProductHeaders(
  options?: AuthRouteRequestInterceptorOptions,
): AxiosHeaders {
  const headers = new AxiosHeaders()
  const appProduct = (options?.appProduct ?? WORKFORCE_APP_PRODUCT).trim()
  if (appProduct.length > 0) {
    headers.set(AUTH_APP_PRODUCT_HEADER, appProduct)
  }
  return headers
}

export type AuthApiCallOptions = AuthRouteRequestInterceptorOptions

/** POST `/auth/login` — no Bearer required (pre-session). */
export async function postLogin(
  client: AxiosInstance,
  request: LoginRequest,
  options?: AuthApiCallOptions,
): Promise<AuthResponse> {
  const res = await client.post<unknown>(
    '/auth/login',
    buildLoginRequestBody(request),
    {
      headers: workforceProductHeaders(options),
    },
  )
  return parseAuthResponse(res.data)
}

/**
 * POST `/auth/refresh-token` — must not send expired Bearer (Flutter omits on this path).
 * Prefer a client with [registerAuthRouteRequestInterceptor] or pass via anonymous config.
 */
export async function postRefreshToken(
  client: AxiosInstance,
  request: RefreshTokenRequest,
  options?: AuthApiCallOptions,
): Promise<AuthResponse> {
  const res = await client.post<unknown>(
    '/auth/refresh-token',
    buildRefreshTokenRequestBody(request),
    {
      headers: workforceProductHeaders(options),
    },
  )
  return parseAuthResponse(res.data)
}

/**
 * POST `/auth/logout` — body-only when access token is expired; omit Bearer.
 */
export async function postLogout(
  client: AxiosInstance,
  request: LogoutRequest,
): Promise<void> {
  await client.post<unknown>(
    '/auth/logout',
    buildLogoutRequestBody(request),
    {
      headers: new AxiosHeaders(),
    },
  )
}

/** GET `/auth/me/session` — requires valid Bearer. */
export async function getMeSession(
  client: AxiosInstance,
): Promise<MeSessionSnapshot> {
  const res = await client.get<unknown>('/auth/me/session')
  return parseMeSessionSnapshot(res.data)
}

/** POST `/auth/staff-invite/validate` — public; returns a display hint for the invite token. */
export async function postValidateStaffInvite(
  client: AxiosInstance,
  token: string,
): Promise<StaffInviteValidation> {
  const res = await client.post<unknown>('/auth/staff-invite/validate', { token })
  return parseStaffInviteValidation(res.data)
}

/** POST `/auth/staff-invite/accept` — public; sets the first password for an invited account. */
export async function postAcceptStaffInvite(
  client: AxiosInstance,
  token: string,
  newPassword: string,
): Promise<{ email: string | null }> {
  const res = await client.post<unknown>('/auth/staff-invite/accept', {
    token,
    newPassword,
  })
  const data = res.data as Record<string, unknown> | null
  const email = data && typeof data === 'object' ? data.email ?? data.Email : null
  return { email: email == null ? null : String(email) }
}
